"use client";

import * as React from "react";
import { CircleAlert as AlertCircle, Check, Cookie, ShieldCheck } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
export default function CookiesPolicy() {
  const [analyticsEnabled, setAnalyticsEnabled] = React.useState(false);
  const [marketingEnabled, setMarketingEnabled] = React.useState(false);
  const [saveStatus, setSaveStatus] = React.useState<"idle" | "success" | "error">("idle");
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(() => {
    try {
      const saved = localStorage.getItem("cookie_preferences");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (typeof parsed.analytics === "boolean") {
          setAnalyticsEnabled(parsed.analytics);
        }
        if (typeof parsed.marketing === "boolean") {
          setMarketingEnabled(parsed.marketing);
        }
      }
    } catch {
      // Ignore reading error if localStorage is restricted
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);
  const persistToStorage = (analytics: boolean, marketing: boolean) => {
    try {
      localStorage.setItem("cookie_preferences", JSON.stringify({
        analytics,
        marketing
      }));
    } catch {
      // Ignore background storage write failure
    }
  };
  const handleAnalyticsChange = (checked: boolean) => {
    setAnalyticsEnabled(checked);
    persistToStorage(checked, marketingEnabled);
  };
  const handleMarketingChange = (checked: boolean) => {
    setMarketingEnabled(checked);
    persistToStorage(analyticsEnabled, checked);
  };
  const handleSave = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    try {
      localStorage.setItem("cookie_preferences", JSON.stringify({
        analytics: analyticsEnabled,
        marketing: marketingEnabled
      }));
      setSaveStatus("success");
    } catch {
      setSaveStatus("error");
    }
    timerRef.current = setTimeout(() => {
      setSaveStatus("idle");
    }, 3000);
  };
  return <section id="cookies-policy" data-nav="light" className="bg-background text-foreground py-16 sm:py-20">
      <div className="mx-auto max-w-[1136px] px-6">
        <div className="mx-auto max-w-3xl">
          {/* Signature Hairline Rule with Turquoise Terminal Dot */}
          <div className="relative mb-8 flex items-center">
            <div className="h-px w-full bg-border" />
            <div className="size-2 rounded-full bg-primary" />
          </div>

          {/* Heading and Date */}
          <div className="mb-10 space-y-3">
            <h1 className="font-default text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              Cookie-Richtlinie
            </h1>
            <p className="text-sm font-medium text-muted-foreground">
              Zuletzt aktualisiert: Januar 2025
            </p>
          </div>

          {/* Continuous Legal Text */}
          <div className="space-y-6 text-base leading-relaxed text-foreground/90">
            <p>
              Diese Cookie-Richtlinie erläutert, wie Hurghada Transfer Hub Cookies
              und ähnliche Browser-Tracking-Technologien verwendet, um sicherzustellen,
              dass unsere Website reibungslos funktioniert, Sitzungsinformationen speichert
              und den Nutzerverkehr analysiert.
            </p>

            <div className="space-y-2">
              <h2 className="font-default text-lg font-semibold text-foreground">
                Was sind Cookies?
              </h2>
              <p>
                Cookies sind kleine Textdateien, die auf Ihrem Computer oder Mobilgerät
                gespeichert werden, wenn Sie unsere Website besuchen. Sie helfen dabei,
                Ihre bevorzugten Transferrouten zu speichern und die Navigation auf der
                Website insgesamt zu verbessern.
              </p>
            </div>

            <div className="space-y-2">
              <h2 className="font-default text-lg font-semibold text-foreground">
                Erforderliche Cookies
              </h2>
              <p>
                Diese Cookies sind für den technischen Betrieb unseres Buchungsformulars,
                die Sicherheitsprotokolle und das Sitzungsrouting zwingend erforderlich.
                Sie können erforderliche Cookies nicht deaktivieren, ohne die Funktionalität
                der Website zu beeinträchtigen.
              </p>
            </div>

            <div className="space-y-2">
              <h2 className="font-default text-lg font-semibold text-foreground">
                Analyse- &amp; Performance-Cookies
              </h2>
              <p>
                Wir nutzen datenschutzkonforme Analysen, um Besucherinteraktionen,
                beliebte Transferrouten und das Nutzungsverhalten auszuwerten, um
                unsere Online-Dienste kontinuierlich zu optimieren.
              </p>
              <p>
                Aktuell lädt diese Website keine Analyse- oder Marketing-Tracking-Skripte, weshalb diese Einstellungen derzeit noch keine aktive Auswirkung haben.
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="my-10 h-px w-full bg-border" />

          {/* Cookie Preferences Section */}
          <div className="rounded-md border border-border bg-card p-6 shadow-xs sm:p-8">
            <div className="mb-6 space-y-1.5">
              <h2 className="font-default text-xl font-semibold text-card-foreground">
                Ihre Cookie-Einstellungen
              </h2>
              <p className="text-sm text-muted-foreground">
                Nutzen Sie die folgenden Optionen, um Ihre bevorzugten Einstellungen festzulegen. Ihre Auswahl wird gespeichert und greift automatisch, falls künftig entsprechende Tracking- oder Analyse-Skripte eingebunden werden.
              </p>
            </div>

            <div className="divide-y divide-border">
              {/* Option: Essential */}
              <div className="flex items-center justify-between py-4">
                <div className="pr-4">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="size-4 text-primary" />
                    <span className="font-default text-sm font-medium text-foreground">
                      Erforderliche Plattform-Cookies (Immer aktiv)
                    </span>
                  </div>
                </div>
                <Switch checked={true} disabled aria-label="Erforderliche Plattform-Cookies immer aktiv" />
              </div>

              {/* Option: Analytics */}
              <div className="flex items-center justify-between py-4">
                <div className="pr-4">
                  <div className="flex items-center gap-2">
                    <Cookie className="size-4 text-primary" />
                    <label htmlFor="analytics-cookie-toggle" className="cursor-pointer font-default text-sm font-medium text-foreground">
                      Performance- &amp; Analyse-Cookies
                    </label>
                  </div>
                </div>
                <Switch id="analytics-cookie-toggle" checked={analyticsEnabled} onCheckedChange={handleAnalyticsChange} aria-label="Performance- und Analyse-Cookies umschalten" />
              </div>

              {/* Option: Marketing */}
              <div className="flex items-center justify-between py-4">
                <div className="pr-4">
                  <div className="flex items-center gap-2">
                    <Cookie className="size-4 text-primary" />
                    <label htmlFor="marketing-cookie-toggle" className="cursor-pointer font-default text-sm font-medium text-foreground">
                      Marketing-Cookies
                    </label>
                  </div>
                </div>
                <Switch id="marketing-cookie-toggle" checked={marketingEnabled} onCheckedChange={handleMarketingChange} aria-label="Marketing-Cookies umschalten" />
              </div>
            </div>

            {/* Actions */}
            <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <Button type="button" onClick={handleSave} className="w-full sm:w-auto">
                Cookie-Einstellungen speichern
              </Button>
              {saveStatus === "success" && <div className="flex items-center gap-1.5 text-xs font-medium text-primary">
                  <Check className="size-4 text-primary" />
                  <span>Einstellungen erfolgreich gespeichert.</span>
                </div>}
              {saveStatus === "error" && <div className="flex items-center gap-1.5 text-xs font-medium text-destructive">
                  <AlertCircle className="size-4 text-destructive" />
                  <span>Fehler beim Speichern der Einstellungen. Bitte prüfen Sie Ihre Browser-Einstellungen.</span>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </section>;
}